import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Network } from 'lucide-react';
import type { NetworkPerformance } from '../../types/dashboard.types';

interface NetworkComparisonChartProps {
  data: NetworkPerformance[];
  isLoading?: boolean;
  className?: string;
}

const NetworkComparisonChart: React.FC<NetworkComparisonChartProps> = ({
  data,
  isLoading = false,
  className = '',
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
          </div>
          <div className="h-80 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <Network className="h-5 w-5 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Network Comparison
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Performance across affiliate networks
          </p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#374151"
            opacity={0.1}
          />
          <XAxis
            dataKey="networkName"
            stroke="#9CA3AF"
            style={{ fontSize: '12px' }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            tickFormatter={formatCurrency}
            stroke="#9CA3AF"
            style={{ fontSize: '12px' }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1F2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#F9FAFB',
            }}
            formatter={(value: number, name: string) => {
              if (name === 'earnings') return [formatCurrency(value), 'Earnings'];
              if (name === 'ftds') return [value, 'FTDs'];
              if (name === 'players') return [value, 'Players'];
              return [value, name];
            }}
          />
          <Legend />
          <Bar
            dataKey="earnings"
            fill="#3B82F6"
            radius={[8, 8, 0, 0]}
            name="Earnings"
          />
          <Bar
            dataKey="ftds"
            fill="#10B981"
            radius={[8, 8, 0, 0]}
            name="FTDs"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default NetworkComparisonChart;
